import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  :root {
    --bg-base: #0f172a;
    --surface: #1e293b;
    --surface-hover: #334155;
    --border: #334155;
    --border-focus: #6366f1;
    --primary: #6366f1;
    --primary-hover: #4f46e5;
    --primary-dim: rgba(99, 102, 241, 0.15);
    --text-main: #f8fafc;
    --text-muted: #94a3b8;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .auth-page {
    min-height: 100vh;
    background: var(--bg-base);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', sans-serif;
    padding: 2rem 1rem;
    position: relative;
    overflow: hidden;
  }

  .auth-page::before {
    content: '';
    position: absolute;
    top: -10%;
    left: 50%;
    transform: translateX(-50%);
    width: 80vw;
    height: 80vh;
    background: radial-gradient(circle, var(--primary-dim) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .auth-card {
    position: relative;
    z-index: 1;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 3rem 2.5rem;
    width: 100%;
    max-width: 440px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  }

  .auth-logo-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
  .auth-logo-icon { width: 28px; height: 28px; color: var(--primary); }
  .auth-logo-text { font-size: 1.5rem; font-weight: 700; letter-spacing: -0.03em; color: var(--text-main); }

  .auth-header { text-align: center; margin-bottom: 2.5rem; }
  .auth-title { font-size: 1.75rem; font-weight: 600; color: var(--text-main); margin-bottom: 0.5rem; letter-spacing: -0.02em; }
  .auth-subtitle { color: var(--text-muted); font-size: 0.95rem; line-height: 1.5; }

  .field-wrap { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 1.8rem; }
  .field-label { font-size: 0.85rem; font-weight: 500; color: var(--text-main); }

  .auth-input {
    width: 100%;
    background: var(--bg-base);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text-main);
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    padding: 0.85rem 1rem;
    outline: none;
    transition: all 0.2s ease;
  }
  .auth-input:focus { border-color: var(--border-focus); box-shadow: 0 0 0 4px var(--primary-dim); }
  .auth-input::placeholder { color: var(--text-muted); }

  .auth-btn {
    width: 100%;
    background: var(--primary);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 0.85rem 1.5rem;
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.2s;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
  }
  .auth-btn:hover:not(:disabled) { background: var(--primary-hover); transform: translateY(-1px); box-shadow: 0 6px 16px rgba(99, 102, 241, 0.35); }
  .auth-btn:disabled { background: var(--surface-hover); color: var(--text-muted); cursor: not-allowed; box-shadow: none; transform: none; }

  .auth-footer-text { text-align: center; margin-top: 2rem; font-size: 0.9rem; color: var(--text-muted); }
  .auth-footer-text a { color: var(--text-main); text-decoration: none; font-weight: 500; transition: color 0.2s; }
  .auth-footer-text a:hover { color: var(--primary); }
`;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${StandardURL}/auth/forgot-pwd`,
        { email }
      );
      navigate("/");
      setIsLoading(false);
      toast.success(response.data.response);
    } catch (error) {
      // Safely access the error response using optional chaining
      const errorMessage = error.response?.data?.response || "Failed to send reset link. Please try again later.";
      
      toast.error(errorMessage);
      setIsLoading(false);
      console.error("Forgot password error:", error);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo-container">
            <svg className="auth-logo-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            <span className="auth-logo-text">Multiphasic Infraconstruct</span>
          </div>

          <div className="auth-header">
            <h2 className="auth-title">Reset Password</h2>
            <p className="auth-subtitle">Enter your email address and we'll send you a link to reset your password.</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="field-wrap">
              <label htmlFor="email" className="field-label">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={handleChange}
                autoComplete="email"
                required
                className="auth-input"
                placeholder="name@company.com"
              />
            </div>

            <button type="submit" disabled={isLoading} className="auth-btn">
              {isLoading ? (
                <Oval height={20} width={20} color="#ffffff" visible={true} ariaLabel="loading-indicator" secondaryColor="rgba(255,255,255,0.3)" strokeWidth={4} strokeWidthSecondary={4} />
              ) : (
                "Send Reset Link"
              )}
            </button>
          </form>

          <p className="auth-footer-text">
            Remembered your password?{" "}
            <a href="/">Back to login</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;